import { apiGet, apiPatch, apiPost } from '@/services/apiClient';
import { tokenStore } from '@/services/tokenStore';
import type { AuthUser } from '@/types/domain';

export interface LoginRequest {
  /** Username or email — the backend resolves whichever matches. */
  identifier: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber?: string;
  address?: string;
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  address?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  async login(payload: LoginRequest): Promise<AuthUser> {
    const result = await apiPost<AuthResponse, LoginRequest>('/auth/login', payload);
    tokenStore.set(result.accessToken);
    return result.user;
  },

  async register(payload: RegisterRequest): Promise<AuthUser> {
    const result = await apiPost<AuthResponse, RegisterRequest>('/auth/register', payload);
    tokenStore.set(result.accessToken);
    return result.user;
  },

  async logout(): Promise<void> {
    try {
      await apiPost<void>('/auth/logout');
    } finally {
      tokenStore.clear();
    }
  },

  async me(): Promise<AuthUser> {
    return apiGet<AuthUser>('/auth/me');
  },

  /**
   * Update the current user's profile fields. Backend endpoint
   * {@code PATCH /api/v1/auth/me} is planned but not yet implemented —
   * once it exists, this call will hit it directly.
   */
  async updateProfile(payload: UpdateProfilePayload): Promise<AuthUser> {
    return apiPatch<AuthUser, UpdateProfilePayload>('/auth/me', payload);
  },

  /**
   * Change the current user's password. Backend endpoint
   * {@code POST /api/v1/auth/me/password} is planned but not yet
   * implemented.
   */
  async changePassword(payload: ChangePasswordPayload): Promise<void> {
    await apiPost<void, ChangePasswordPayload>('/auth/me/password', payload);
  },
};
